import React, { FC, useMemo } from 'react'
import OrderCardItem, { Props } from '~/app/components/order-list/common/OrderCardItem'
import { styled, TextButton } from '~/app/components'
import { SERVICE_TYPE, STATUS, statusToString } from '~/app/utils/OrderListUtils'
import { WarningNotice } from '~/app/components/Cart/CartItem/CartItem.styles'
import OrderCardItemButtons from '~/app/components/order-list/common/OrderCardItemButtons'
import PendingReservationNotice from '~/app/components/order-list/common/PendingReservationNotice'
import { toKRW } from '~/app/utils/krNumberToString'
import LeisureCancelPolicy from '~/app/components/common/Leisure/LeisureCancelPolicy'
import useCancelCommission from '~/app/components/order-list/hooks/useCancelCommission'
import { isLeisureCategoryType } from '~/app/components/order-converter/converter/LeisureOrderConverter'
import CancelPolicyText, {
  CancelPolicyFetchFailText,
} from '~/app/components/common/CancelPolicy/CancelPolicyText'
import LeisureCancelRequestedNotice from '~/app/components/order-list/common/LeisureCancelRequestedNotice'
import { Icon_error } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'
import FetchRetryButton from '~/app/components/common/FetchRetry/FetchRetryButton'
import useModalHash from '~/app/components/common/useModalHash'
import OrderPriceDetailsBottomsheet from '~/app/components/order-list/details/OrderPriceDetailsBottomsheet'

/**
 * OrderDetailCardItem component
 * author: soojin.oh
 */

// type definitions
export type OrderDetailCardItemProps = {
  id: string
  isFirstItem?: boolean
  isShowCancelPolicy?: boolean
  onButtonClick?: (id: string, data?: any) => void
  onProductClick?: (id: string, url: string, deeplink?: string) => void
} & Props

// styled components
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    width: calc(50% - 3rem);
    border: 0.1rem solid ${({ theme }) => theme.color.gray200};
    border-radius: 0.4rem;
    margin: 0 0 2rem 2rem;
    min-height: 21.3rem;
  }
`

const Divider = styled.div`
  border-bottom: 0.1rem solid ${({ theme }) => theme.color.gray200};
  margin: 0 2rem;

  @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
    display: none;
  }
`
const PriceDetail = styled.div`
  display: flex;
  margin: 0 2rem 0 8.4rem;
  flex-direction: row;
  justify-content: flex-end;
  text-decoration: underline;
`
const Price = styled.div`
  display: flex;
  ${Typography('body1').bold};
  color: ${({ theme }) => theme.color.gray900};
  margin: 0 2rem 0 8.4rem;
  flex-direction: row;
  justify-content: space-between;

  & .type {
    ${Typography('body2').regular};
    color: ${({ theme }) => theme.color.gray600};
    margin: 0.1rem 0.4rem 0.1rem 0;
  }

  & .priceWrapperTwo {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  & .priceWrapperOne {
    margin-left: auto;
  }

  &.cancel-completed,
  &.completed,
  &.cancel-requested {
    color: ${({ theme }) => theme.color.gray400};
    .type {
      color: ${({ theme }) => theme.color.gray400};
    }
  }
`
const Promotion = styled.div`
  ${Typography('body2').regular};
  text-align: right;
  color: #e31068;
  margin-right: 2rem;
  margin-top: 0.2rem;
`

const ButtonContainer = styled.div`
  margin-top: 1.2rem;
  padding: 0 2rem 2rem 8.4rem;

  &:empty {
    margin-top: 0;
  }
`

const PendingReservationNoticeContainer = styled.div`
  margin: -2rem 2rem 2rem;
`

const WarningNotice2 = styled(WarningNotice)`
  padding-left: 8.4rem;

  span {
    margin-top: 0.1rem;
  }
`

const OrderDetailCancelPolicyFetchFailText = styled(CancelPolicyFetchFailText)`
  margin-left: 8.4rem;
`

const CancelWrapper = styled.div`
  margin-right: 2rem;
`

const LeisureCancelPolicyWrapper = styled.div`
  margin-top: 0.4rem;
  & > span {
    width: 100%;
  }
`

const CancelFeeWrapper = styled.div`
  margin-top: 0.4rem;

  & > div {
    margin-top: 0;
  }
`

const LeisureCancelRequestedNoticeWrapper = styled.div`
  padding: 0 2rem;
`

const NoticeWrapper = styled.div`
  margin-bottom: 1rem;
`

// OrderDetailCardItem component
const OrderDetailCardItem: FC<OrderDetailCardItemProps> = function ({
  id,
  categoryOrderId,
  serviceType,
  categoryType,
  imageSrc,
  title,
  subTitle,
  ratePlanTitle,
  status,
  info,
  options,
  price,
  promotion,
  buttons,
  onButtonClick,
  onButtonImpressed,
  onProductClick,
  isFirstItem,
  pdpUrl,
  deeplink,
  busTransportationCompanyCode,
  isDisabledLazyLoadImage,
  useTypeText,
  isInfiniteCouponRoom,
  cancelPolicySummary,
  hasPenalty,
  hasPartialCancel,
  badges,
  isPendingReservation,
  refundInfo,
  isShowLeisureCancelRequestNotice,
  isShowCancelPolicy = true,
  propertyCategory,
  memberClassBadge,
  cancellationFeePlans,
  optionTitle,
  dailyPrices,
}) {
  const isLeisureCategory = useMemo(() => isLeisureCategoryType(categoryType), [categoryType])
  const { cancelSummary } = useCancelCommission(cancelPolicySummary)
  const isShowCancelPolicySummaryRetry = useMemo(
    () => !cancelSummary && !isLeisureCategory,
    [cancelSummary, isLeisureCategory]
  )
  const [isPriceDetailOpened, setPriceDetailOpened] = useModalHash({ id: 'order-price-detail' })
  const hasDailyPrices = !!dailyPrices && dailyPrices.length > 1
  return (
    <Container>
      {!isFirstItem && <Divider />}
      <OrderCardItem
        id={id}
        className={'clickable'}
        categoryOrderId={categoryOrderId}
        status={status}
        serviceType={serviceType}
        categoryType={categoryType}
        imageSrc={imageSrc}
        title={title}
        subTitle={subTitle}
        optionTitle={optionTitle}
        ratePlanTitle={ratePlanTitle}
        info={info}
        options={options}
        onClick={onProductClick}
        pdpUrl={pdpUrl}
        deeplink={deeplink}
        busTransportationCompanyCode={busTransportationCompanyCode}
        isDisabledLazyLoadImage={isDisabledLazyLoadImage}
        isInfiniteCouponRoom={isInfiniteCouponRoom}
        /**
         *   예약상세 취소 규정은 OrderDetailCardItem 에서 별도로 노출하기에
         *   showCancelPolicyRetry 는 false, cancelPolicySummary 는 전달하지 않음
         */
        showCancelPolicyRetry={false}
        badges={badges}
        isPendingReservation={isPendingReservation}
        fromDetail={true}
        memberClassBadge={memberClassBadge}
      />
      <div>
        {(isShowCancelPolicySummaryRetry || !!hasPartialCancel) && (
          <NoticeWrapper>
            {isShowCancelPolicySummaryRetry && <OrderDetailCancelPolicyFetchFailText />}
            {!!hasPartialCancel && (
              <WarningNotice2>
                <Icon_error size={1.6} unit="rem" />
                <span>이용 기간 중 일부는 예약이 취소됐어요</span>
              </WarningNotice2>
            )}
          </NoticeWrapper>
        )}
        <Price className={statusToString(status)}>
          {isShowCancelPolicySummaryRetry && (
            <FetchRetryButton onClick={() => onButtonClick?.('cancel-policy-refresh', true)} />
          )}
          <div className={isShowCancelPolicySummaryRetry ? 'priceWrapperTwo' : 'priceWrapperOne'}>
            {serviceType === SERVICE_TYPE.ACCOMMODATION && (
              <span className={'type'}>{useTypeText}</span>
            )}
            {/* 가격이 없는 케이스가 있는지 모르겠지만 가격이 없다면 0원 으로 표시함 */}
            <span>{toKRW(price ?? 0)}</span>
          </div>
        </Price>
        {hasDailyPrices && (
          <PriceDetail>
            <TextButton
              type={'basic'}
              typography={'body2'}
              title={'상세요금 보기'}
              onClick={() => setPriceDetailOpened(true)}
            />
          </PriceDetail>
        )}
        {isShowCancelPolicy && (
          <CancelWrapper>
            {isLeisureCategory ? (
              <LeisureCancelPolicyWrapper>
                <LeisureCancelPolicy
                  cancellationFeePlans={cancellationFeePlans ?? []}
                  refundInfo={refundInfo}
                  isAvailableUse={status === STATUS.READY}
                />
              </LeisureCancelPolicyWrapper>
            ) : (
              cancelSummary &&
              !hasPenalty && (
                <CancelFeeWrapper>
                  <CancelPolicyText
                    cancelPolicySummary={cancelSummary}
                    propertyCategory={propertyCategory}
                    isPendingReservation={isPendingReservation}
                  />
                </CancelFeeWrapper>
              )
            )}
          </CancelWrapper>
        )}
        {promotion && <Promotion>{promotion}</Promotion>}
        {isShowLeisureCancelRequestNotice && (
          <LeisureCancelRequestedNoticeWrapper>
            <LeisureCancelRequestedNotice />
          </LeisureCancelRequestedNoticeWrapper>
        )}
        <ButtonContainer>
          {!!buttons?.length && (
            <OrderCardItemButtons
              buttons={buttons}
              onButtonClick={(id, data) => onButtonClick?.(id, data)}
              onButtonImpressed={(id, data) => onButtonImpressed?.(id, data)}
            />
          )}
        </ButtonContainer>
        {isPendingReservation && (
          <PendingReservationNoticeContainer>
            <PendingReservationNotice />
          </PendingReservationNoticeContainer>
        )}
      </div>
      {hasDailyPrices && (
        <OrderPriceDetailsBottomsheet
          isOpened={isPriceDetailOpened}
          onRequestClose={() => setPriceDetailOpened(false)}
          dailyPrices={dailyPrices}
        />
      )}
    </Container>
  )
}

export default OrderDetailCardItem
